import {defer, redirect, type LoaderArgs} from '@shopify/remix-oxygen';
import {useLoaderData} from '@remix-run/react';
import {
  AnalyticsPageType,
  ShopifyAnalyticsProduct,
  flattenConnection,
  type SeoHandleFunction,
  type SeoConfig,
} from '@shopify/hydrogen';
import {Heading, Link, ProductGallery, Text} from '~/modules';
import {v4 as uuidv4} from 'uuid';

import type {
  SelectedOptionInput,
  MediaConnection,
  MediaImage,
} from '@shopify/hydrogen/storefront-api-types';

import type {Product} from 'schema-dts';
import {ProductDetail} from '~/modules/Product/components/ProductDetail';
import {ProductPrice} from '~/modules/Product/components/ProductPrice';
import {ProductForm} from '~/modules/Product/components/ProductForm';
import {ProductsProvider} from '~/modules/Product/context/ProductsContext';
import {useProduct} from '~/modules/Product/hooks/useProduct';

import {getLoaderExpertName} from '~/modules/Common/util';

import {Container} from '~/modules/Layout/components/Container';
import {TrustMarks} from '~/modules/Product/components/TrustMarks';
import {CloudImage} from '~/modules/Common/components/CloudImage';
import {BaseLayout} from '~/modules/Layout/components/BaseLayout';
import {usePDPanalytics} from '~/modules/Product/hooks/usePDPanalytics';
import {useProductImage} from '~/modules/Product/hooks/useProductImage';
import {getProductQuery} from '~/modules/Product/utils/getProductQuery.server';
import {useBrandCollectionLink} from '~/modules/Common/hooks/useCollectionLink';
import {MilkDrunkCallout} from '~/modules/Common/components/MilkDrunkCallout';
import {useRudderStack} from '~/modules/Common/hooks/useRudderstack';
import {useEffect} from 'react';
import {AffiliateSubType, ShoppingType} from '~/gql/generated';
import {AnalyticsEvent} from '~/modules/Common/hooks/analyticsEvent';
import {track} from '~/modules/Common/analytics.server';

const seo: SeoHandleFunction<typeof loader> = ({data}) => {
  const media = data.shopifyProduct
    ? (flattenConnection<MediaConnection>(data.shopifyProduct.media).find(
        (media) => media.mediaContentType === 'IMAGE',
      ) as MediaImage)
    : undefined;
  const product = data?.product;

  return product
    ? ({
        title: `${product.brandName || ''} ${product.name || ''}`,
        media: media?.image,
        description: (
          product?.description ||
          product.descriptionHtml ||
          ''
        ).substring(0, 155),
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'Product',
          brand: product?.brandName || '',
          name: product?.name || '',
        },
      } satisfies SeoConfig<Product>)
    : {};
};

export const handle = {
  seo,
};

export const getLoader =
  (isLander: boolean) =>
  async ({params, request, context}: LoaderArgs) => {
    const expertName = getLoaderExpertName(params, request);
    if (!expertName) {
      return redirect('/404');
    }

    // This ensures we use the correct product ID for the query
    // Some products are only in PRT, but historically the URL has the shopify ID (handle) first
    const productHandle = !params.productId
      ? undefined
      : (params.productHandle as string);
    const productId = params.productId
      ? (params.productId as string)
      : (params.productHandle as string);

    const searchParams = new URL(request.url).searchParams;
    const selectedOptions: SelectedOptionInput[] = [];
    searchParams.forEach((value, name) => {
      selectedOptions.push({name, value});
    });

    const {product, shopifyProduct, shopifyProducts, selectedVariant} =
      await getProductQuery({
        productId,
        handle: productHandle,
        options: {
          selectedOptions,
          client: context.client,
          storefront: context.storefront,
          expertName,
          isLander,
          isPreview: searchParams.get('preview') === 'true',
        },
      });

    if (
      (productHandle && !shopifyProduct?.id) ||
      !product ||
      !product?.isActive
    ) {
      console.error('No data found for product');
      throw new Response(null, {
        status: 404,
        statusText: 'The product you are looking for is no longer here.',
      });
    }

    if (product.affiliateSubType === AffiliateSubType.Impact) {
      await track(context.env, {
        anonymousId: uuidv4(), // user wont have client analytics ever fire
        event: AnalyticsEvent.affiliateProductClick,
        properties: {
          product,
          expertName,
          userAgent: request.headers.get('user-agent') || '',
          referrer: request.headers.get('referer') || '',
          source: 'redirect',
        },
        timestamp: new Date().toISOString(),
      });
      return redirect(product.outlink);
    }

    if (
      product.affiliateSubType === AffiliateSubType.Amazon &&
      (request.headers.get('user-agent') || '')
        .toLocaleLowerCase()
        .includes('instagram')
    ) {
      await track(context.env, {
        anonymousId: uuidv4(), // user wont have client analytics ever fire
        event: AnalyticsEvent.affiliateProductClick,
        properties: {
          product,
          expertName,
          userAgent: request.headers.get('user-agent') || '',
          referrer: request.headers.get('referer') || '',
          source: 'redirect',
        },
        timestamp: new Date().toISOString(),
      });
      return redirect(
        product.outlink.replace('https:', 'com.amazon.mobile.shopping.web:'),
      );
    }

    const price =
      typeof selectedVariant?.price === 'object'
        ? selectedVariant?.price?.amount
        : product.price;
    const productAnalytics: ShopifyAnalyticsProduct = {
      productGid: shopifyProduct?.id || product.id,
      variantGid: selectedVariant?.id || product.id,
      name: shopifyProduct?.title || product.name,
      variantName: selectedVariant?.title || product.name,
      brand: product.brandName,
      price,
    };

    return defer({
      product,
      shopifyProduct,
      shopifyProducts,
      analytics: {
        pageType: AnalyticsPageType.product,
        resourceId: shopifyProduct?.id,
        products: [productAnalytics],
        totalValue: parseFloat(price || 0),
      },
    });
  };
export const loader = getLoader(false);

export default function Product() {
  const {shopifyProducts, product} = useLoaderData<typeof loader>();
  const {trackEvent} = useRudderStack();
  useEffect(() => {
    trackEvent(AnalyticsEvent.Contact);
  }, []);

  return (
    <BaseLayout>
      <ProductsProvider
        shopifyProducts={shopifyProducts?.nodes || []}
        apiProducts={[product]}
      >
        <PDPLayout />
      </ProductsProvider>
    </BaseLayout>
  );
}

export function PDPLayout() {
  const product = useProduct();
  const firstImage = useProductImage(product!);
  usePDPanalytics();
  const brandLink = useBrandCollectionLink(product.brandSlug);

  if (!product) {
    return null;
  }

  const productName = product.name;
  const BrandNameComponent = (
    <Text as="p" className={'font-bold text-sm text-codGray800'}>
      {product.brandName}
    </Text>
  );

  return (
    <Container>
      <MilkDrunkCallout />
      <section
        id={'pdplayout'}
        className="w-full gap-4 md:gap-8 grid pb-2 border-none  md:p-4 lg:pb-8"
      >
        <div className="grid items-start md:gap-6 lg:gap-10 md:grid-cols-2 lg:grid-cols-3 overflow-hidden">
          <ProductGallery
            media={product.media}
            firstImage={firstImage}
            className="w-screen md:w-full lg:col-span-2"
            isAmazonImage={
              product.brandType === AffiliateSubType.Amazon &&
              product.affiliateSubType !== AffiliateSubType.Amazon
            }
          />
          <section className="flex flex-col w-full max-w-xl gap-3 px-4 pt-2 md:mx-auto md:max-w-sm ">
            <div className="grid">
              <div className="flex flex-row flex-nowrap gap-2 items-center">
                {product.brandLogoUrl && (
                  <div className="h-[45px]  rounded-full overflow-hidden mb-1">
                    <CloudImage
                      visibleByDefault
                      src={product.brandLogoUrl}
                      alt={product.brandName}
                      height={200}
                      quality={'100'}
                      crop="pad"
                      style={{maxHeight: '45px'}}
                    />
                  </div>
                )}
                {product.brandName && !product.brandSlug && BrandNameComponent}
                {product.brandName && product.brandSlug && (
                  <Link to={brandLink}>{BrandNameComponent}</Link>
                )}
              </div>
              <Heading
                as="h1"
                className="whitespace-normal text-2xl"
                size="heading"
              >
                {productName}
              </Heading>
              <ProductPrice />
            </div>
            <ProductForm />
            <TrustMarks
              brand={product.brandName}
              brandId={product.brandId}
              shippingSourceCountry={product.brandShippingSourceCountry}
              shoppingType={
                product.brandType?.toLowerCase() ===
                AffiliateSubType.Amazon.toLowerCase()
                  ? ShoppingType.Affiliate
                  : product.shoppingType
              }
              product={product}
            />
            <ProductDetail />
          </section>
        </div>
      </section>
    </Container>
  );
}
